/**
 * Making a view, and doing something to the one on screen.
 *
 * A view is the only way to look at a table -- the rows endpoint is asked for
 * one, never for the table -- so "this table has no views" was a dead end until
 * there was a way to make one. The menu is therefore shown even when there is
 * no view to act on, with only the one item that still means something.
 *
 * Every view type the build can open is offered, which is now all seven.
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  ArrowUpDown,
  Calendar,
  ChartGantt,
  Columns3,
  Copy,
  Download,
  Image,
  List,
  Info,
  MoreHorizontal,
  Table2,
  TextCursorInput,
  Trash2,
  type LucideIcon,
  Settings2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, View, ViewTypeName } from '@/api/types'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { DetailsDialog } from '@/shared/components/details-dialog'
import { NameDialog } from '@/shared/components/name-dialog'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { ExportDialog } from '../export/export-dialog'
import { DefaultValuesDialog } from './default-values-dialog'
import { ReorderViewsDialog } from './reorder-views-dialog'
import { useViewMutations } from './view-mutations'

type Dialog = 'create' | 'details' | 'delete' | 'reorder' | 'export' | 'defaults'

/**
 * The view types that can be made, with what they are called when made.
 *
 * A list rather than one item each, so adding a surface is adding a line --
 * and so the entries cannot drift apart in wording or behaviour.
 *
 * The icons are the ones the switcher uses for the same types, because a view
 * is picked out of that list by the icon it was made with.
 */
const MAKEABLE = [
  { type: 'grid', icon: Table2, label: 'view.newGrid', about: 'view.aboutGrid' },
  { type: 'form', icon: TextCursorInput, label: 'view.newForm', about: 'view.aboutForm' },
  { type: 'gallery', icon: Image, label: 'view.newGallery', about: 'view.aboutGallery' },
  { type: 'board', icon: Columns3, label: 'view.newBoard', about: 'view.aboutBoard' },
  {
    type: 'schedule',
    icon: Calendar,
    label: 'view.newSchedule',
    about: 'view.aboutSchedule',
  },
  { type: 'gantt', icon: ChartGantt, label: 'view.newGantt', about: 'view.aboutGantt' },
  { type: 'list', icon: List, label: 'view.newList', about: 'view.aboutList' },
] as const satisfies readonly {
  type: ViewTypeName
  icon: LucideIcon
  label: string
  /** One line on what the type does, shown while it is being named. */
  about: string
}[]

export function ViewActions({
  tableId,
  databaseId,
  view,
  views,
  fields,
}: {
  tableId: number
  databaseId: number
  /** The view on screen, or nothing when the table has none to show. */
  view: View | undefined
  /** Every view of the table, so a deleted one can be replaced on screen. */
  views: readonly View[]
  /** The table's schema, which the default values are set against. */
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<Dialog | null>(null)
  // The entry rather than the type name, because the dialog names and draws
  // the type as well as making it.
  const [making, setMaking] = useState<(typeof MAKEABLE)[number]>(MAKEABLE[0])
  const { create, updateDetails, duplicate, remove, reorder, isPending } =
    useViewMutations(tableId)

  /** Goes to a view, or to the table's own address when there is none left. */
  function show(viewId: number | undefined): void {
    void navigate({
      to: '/database/$databaseId/table/$tableId',
      params: { databaseId, tableId },
      // Everything else in the address belongs to the view being left: a
      // search narrows the rows of one view, and an open row is one of them.
      search: { view: viewId },
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('view.actions')}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {MAKEABLE.map((entry) => {
            const { type, icon: Icon, label } = entry
            return (
              <DropdownMenuItem
                key={type}
                onSelect={() => {
                  setMaking(entry)
                  setDialog('create')
                }}
              >
                <Icon className="size-4" />
                {t(label)}
              </DropdownMenuItem>
            )
          })}

          {/* Only when there is more than one, because the order of one view
              is not a thing anybody can change. */}
          {views.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  setDialog('reorder')
                }}
              >
                <ArrowUpDown className="size-4" />
                {t('view.reorder')}
              </DropdownMenuItem>
            </>
          )}

          {view !== undefined && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  setDialog('details')
                }}
              >
                <Info className="size-4" />
                {t('action.details')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  duplicate(view.id, (copy) => {
                    show(copy.id)
                  })
                }}
              >
                <Copy className="size-4" />
                {t('action.duplicate')}
              </DropdownMenuItem>
              {/* Above the destructive item and below the two that make a
                  copy, because it is the third thing somebody does *with* the
                  view rather than to it. Offered for every view type, since an
                  export is of the rows and every view has those -- even the
                  ones this build cannot draw yet. */}
              <DropdownMenuItem
                onSelect={() => {
                  setDialog('export')
                }}
              >
                <Download className="size-4" />
                {t('export.title')}
              </DropdownMenuItem>
              {/* A default belongs to the view rather than to the column, which
                  is why it is here and not in the column's own menu. */}
              <DropdownMenuItem
                onSelect={() => {
                  setDialog('defaults')
                }}
              >
                <Settings2 className="size-4" />
                {t('defaults.menu')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => {
                  setDialog('delete')
                }}
              >
                <Trash2 className="size-4" />
                {t('action.delete')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'create' && (
        <NameDialog
          open
          onOpenChange={() => {
            setDialog(null)
          }}
          title={t(making.label)}
          description={t(making.about)}
          icon={making.icon}
          label={t('view.name')}
          currentName=""
          submitLabel={t('action.create')}
          pending={isPending}
          onSubmit={(name) => {
            create(name, making.type, (made) => {
              show(made.id)
            })
            setDialog(null)
          }}
        />
      )}

      {dialog === 'details' && view !== undefined && (
        <DetailsDialog
          open
          onOpenChange={() => {
            setDialog(null)
          }}
          title={t('view.detailsTitle')}
          nameLabel={t('view.name')}
          descriptionLabel={t('view.description')}
          descriptionHint={t('view.descriptionHint')}
          current={{ name: view.name, description: view.description ?? '' }}
          pending={false}
          onSubmit={(details) => {
            updateDetails(view.id, details)
            setDialog(null)
          }}
        />
      )}

      {dialog === 'defaults' && view !== undefined && (
        <DefaultValuesDialog
          tableId={tableId}
          view={view}
          fields={fields}
          onClose={() => {
            setDialog(null)
          }}
        />
      )}

      {dialog === 'export' && (
        <ExportDialog
          tableId={tableId}
          view={view}
          onClose={() => {
            setDialog(null)
          }}
        />
      )}

      {dialog === 'reorder' && (
        <ReorderViewsDialog
          views={views}
          onOpenChange={() => {
            setDialog(null)
          }}
          onReorder={reorder}
        />
      )}

      {dialog === 'delete' && view !== undefined && (
        <ConfirmDialog
          open
          onOpenChange={() => {
            setDialog(null)
          }}
          title={t('view.deleteTitle', { name: view.name })}
          description={t('view.deleteDescription')}
          confirmLabel={t('action.delete')}
          destructive
          onConfirm={() => {
            remove(view.id, () => {
              // Whoever deleted it was looking at it, so the page has to go
              // somewhere: the first view left, or the table's own address when
              // there is none, which is the empty state with the way out in it.
              show(views.find((candidate) => candidate.id !== view.id)?.id)
            })
            setDialog(null)
          }}
        />
      )}
    </>
  )
}
